import {
  type AriaAttributes,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useRef,
} from 'react'
import cx from 'classnames'
import styles from './SplitViewSidebarListItem.module.css'
import { isEmptyNode } from '@/utils/react-util'
import { Link } from '@/components/Link'
import { Icon } from '@/components/Icon'

interface SplitViewSidebarListItemProps {
  children?: ReactNode
  active?: boolean
  visual?: ReactNode
  extras?: ReactNode
  isDisabled?: boolean
  onPress?: MouseEventHandler
  // Link-related props
  to?: string
  href?: string
  replace?: boolean
  ['aria-current']?: AriaAttributes['aria-current']
  spacing?: 'none' | 'compact' | 'default'
  testId?: string
}

/**
 * An item in a split view sidebar list, pressable when it has a destination or handler.
 */
export function SplitViewSidebarListItem({
  children,
  active,
  visual,
  extras,
  to,
  href,
  replace,
  onPress,
  isDisabled,
  spacing = 'default',
  testId,
  ...rest
}: SplitViewSidebarListItemProps) {
  const clickable = !isDisabled && Boolean(to || href || onPress)
  const rootRef = useRef<HTMLAnchorElement | null>(null)
  // Scrolls to the active element when it becomes active.
  useEffect(() => {
    if (active) {
      requestAnimationFrame(() =>
        rootRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        }),
      )
    }
  }, [active])
  return (
    <Link
      ref={rootRef}
      to={to}
      replace={replace}
      href={href}
      onClick={onPress}
      className={cx(
        styles.root,
        active && styles.active,
        clickable && styles.clickable,
        isDisabled && styles.disabled,
        spacing && styles[`spacing-${spacing}`],
      )}
      data-test={testId}
      {...rest}
    >
      {!isEmptyNode(visual) && <div className={styles.visual}>{visual}</div>}
      <div className={styles.main}>{children}</div>
      {!isEmptyNode(extras) && <div className={styles.extras}>{extras}</div>}
    </Link>
  )
}

interface SplitViewSidebarListItemPretitleProps {
  children?: ReactNode
  title?: string
  /**
   * If `true` disables the text cutoff behavior.
   */
  wrap?: boolean
}

/**
 * The line above the title of a split view sidebar list item.
 */
export function SplitViewSidebarListItemPretitle({
  children,
  title,
  wrap,
}: SplitViewSidebarListItemPretitleProps) {
  return (
    <div title={title} className={cx(styles.pretitle, !wrap && styles.cutoff)}>
      {children}
    </div>
  )
}

interface SplitViewSidebarListItemTitleProps {
  children?: ReactNode
  title?: string
  /**
   * If `true` disables the text cutoff behavior.
   */
  wrap?: boolean
}

/**
 * The title of a split view sidebar list item.
 */
export function SplitViewSidebarListItemTitle({
  children,
  title,
  wrap,
}: SplitViewSidebarListItemTitleProps) {
  return (
    <div title={title} className={cx(styles.title, !wrap && styles.cutoff)}>
      {children}
    </div>
  )
}

interface SplitViewSidebarListItemSubtitleProps {
  children?: ReactNode
  title?: string
  /**
   * If `true` disables the text cutoff behavior.
   */
  wrap?: boolean
  testId?: string
}

/**
 * The subtitle of a split view sidebar list item.
 */
export function SplitViewSidebarListItemSubtitle({
  children,
  title,
  wrap,
  testId,
}: SplitViewSidebarListItemSubtitleProps) {
  return (
    <div
      title={title}
      className={cx(styles.subtitle, !wrap && styles.cutoff)}
      data-test={testId}
    >
      {children}
    </div>
  )
}

/**
 * An arrow that marks a split view sidebar list item as navigable.
 */
export function SplitViewSidebarListItemArrow() {
  return (
    <span className={styles.arrow}>
      <Icon name="arrow" />
    </span>
  )
}
